import React, {Fragment, useCallback, useMemo, useState, useEffect} from "react";
import {compose} from "redux";
import {translate} from "react-i18next";
import InfiniteScrollTable from "../../../components/infinite-scroll-table";
import {GUTTER_SM, TABLE_COL_MIN_WIDTH_ID} from "../../../styles/constants";
import {BUTTON_DELETE, BUTTON_DETAIL} from "../../../styles/buttons";
import {Button, Col, Modal, Row, Tooltip} from "antd";
import ConnectionSelector from "./modals/ConnectionSelector";
import {CREATE_MODE, MODIFY_MODE} from "./utils";
import {connect} from "react-redux";
import CreateModifyDataset from "./modals/CreateModifyDataset";
import Call from "../../../hocs/call";
import {
    getDataflowAndCubes,
    getDdbConnectionsDatasetBuilder,
    addCurrentDatasetToConnectionDatasetBuilder,
    datasetBuilderDeleteDataset, getDatasets, getDataflowDatasetPrefix, clearRowsNotEditableDatasetBuilder
} from "./actions";
import {clearDatasetBuilder, DatasetBuilderGetDataset} from "./modals/actions";


const mapStateToProps = (state) => ({
    connId: state.scenes.mappingHandler.datasetBuilder.index.connId,
    connections: state.scenes.mappingHandler.datasetBuilder.index.connections,
    datasets: state.scenes.mappingHandler.datasetBuilder.index.datasets,
    dataflowsAndCubes: state.scenes.mappingHandler.datasetBuilder.index.dataflowsAndCubes,
    selectedTables: state.scenes.mappingHandler.datasetBuilder.unionModalProps.selectedTables,
    isSuccess: state.scenes.mappingHandler.datasetBuilder.index.isSuccess,
    query: state.scenes.mappingHandler.datasetBuilder.unionModalProps.query,
    username: state.app.user.username,
    token: state.app.user.token,
    permissions: state.app.user.permissions,
    email: state.app.user.email,
    prefix: state.scenes.mappingHandler.datasetBuilder.index.prefix,
    rowsNotEditable: state.scenes.mappingHandler.datasetBuilder.index.rowsNotEditable
});

const mapDispatchToProps = dispatch => ({
    getConnections: ({username, permissions, token, email}) => dispatch(getDdbConnectionsDatasetBuilder(
        {
            username,
            permissions,
            token,
            email,
            metadataFlow: [],
            dataflow: [],
            isAuthenticated: true,
            dbbConnections: []
        })),
    getDataflowsAndDatacubes: () => dispatch(getDataflowAndCubes()),
    clearAfterModalHiding: () => dispatch(clearDatasetBuilder()),
    addCurrentDatasetToConnection: (dataset) => dispatch(addCurrentDatasetToConnectionDatasetBuilder(dataset)),
    getDataset: state => dispatch(DatasetBuilderGetDataset(state)),
    deleteDataset: datasetId => dispatch(datasetBuilderDeleteDataset(datasetId)),
    getDatasets: (connId, username) => dispatch(getDatasets(connId, username)),
    getDataflowDatasetPrefix: () => dispatch(getDataflowDatasetPrefix()),
    clearNotEditableRows: () => dispatch(clearRowsNotEditableDatasetBuilder()),
})

// const data = datasetBuilderData();

const DatasetBuilder = ({
                            t,
                            connId,
                            connections,
                            getConnections,
                            getDataflowsAndDatacubes,
                            dataflowsAndCubes,
                            clearAfterModalHiding,
                            datasets = [],
                            username,
                            permissions,
                            token,
                            email,
                            addCurrentDatasetToConnection,
                            getDataset,
                            deleteDataset,
                            getDatasets,
                            isSuccess,
                            getDataflowDatasetPrefix,
                            prefix,
                            rowsNotEditable,
                            clearNotEditableRows
                        }) => {
    const selectedData = useMemo(() => connId && datasets ? datasets : [], [connId, datasets]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalInfo, setModalInfo] = useState({});
    const [disableModify, setDisableModify] = useState(false);
    const [isDataflowBuilder, setIsDataflowBuilder] = useState(false);
    useEffect(() => {
        if (!isModalVisible) {
            setDisableModify(false);
            setIsDataflowBuilder(false);
        }
    }, [isModalVisible]);

    useEffect(() => {
        if (isSuccess) getDatasets(connId, username);
    }, [isSuccess]);

    useEffect(() => {
        if (rowsNotEditable) {
            const temp = [...rowsNotEditable];
            Modal.warning({
                title: t('data.datasetBuilder.rowsNotEditableModal.title'),
                content: (
                    <Fragment>
                        <span>{t('data.datasetBuilder.rowsNotEditableModal.message')}</span>
                        <ul>
                            {temp.map((col, idx) => <li key={idx}>{col}</li>)}
                        </ul>
                    </Fragment>
                )
            })
            clearNotEditableRows();
        }
    }, [rowsNotEditable]);

    const onModalHide = useCallback(() => {
        setIsModalVisible(false);
        setModalInfo({});
        clearAfterModalHiding();
    }, [setIsModalVisible, setModalInfo]);

    const onModalShow = useCallback((mode, datasetId = "") => {
        const selectedDataset = datasetId ? datasets.find(d => d.id === datasetId) : {};
        setModalInfo({
            mode,
            action: (currentDataset, query, connectionId, updateInDbData) => {
                const isNew = !selectedDataset.name || selectedDataset.name !== currentDataset.name;
                addCurrentDatasetToConnection({
                    ...currentDataset,
                    query,
                    connection_Id: connectionId,
                    editor_Type: "CustomQueryEditor",
                    columns: updateInDbData || [],
                    id: isNew ? 0 : currentDataset.id
                })
            },
            dataset: selectedDataset
        });
        setIsModalVisible(true);
    }, [setIsModalVisible, setModalInfo, datasets, connId, username]);

    return (
        <Fragment>
            <Call cb={getDataflowDatasetPrefix}>
                <Call cb={getConnections} cbParam={{username, permissions, token, email}}
                      disabled={!!connections && connections.length > 0}>
                    <Call cb={getDataflowsAndDatacubes} disabled={!!dataflowsAndCubes && dataflowsAndCubes.length > 0}>
                        <CreateModifyDataset isModalVisible={isModalVisible} onModalHide={onModalHide}
                                             disableModify={disableModify} isDataflowBuilder={isDataflowBuilder}
                                             modalInfo={modalInfo}/>
                        <InfiniteScrollTable
                            data={selectedData}
                            getRowKey={({id}) => id}
                            columns={[{
                                title: t('data.datasetBuilder.name.shortLabel'),
                                dataIndex: 'name',
                                minWidth: TABLE_COL_MIN_WIDTH_ID,
                                render: row => {
                                    const textColor = row.startsWith(prefix) ? 'red' : '#686868';
                                    return <span style={{color: textColor}}>
                                            {row}
                                        </span>
                                },
                                renderText: row => row.startsWith(prefix)
                                    ? t('scenes.mappingHandler.DatasetBuilder.wizard.isDataflowBuilder.title')
                                    : row
                                ,
                            }, {
                                title: t('data.datasetBuilder.description.shortLabel'),
                                dataIndex: 'description',
                                minWidth: TABLE_COL_MIN_WIDTH_ID
                            }, {
                                title: t('data.datasetBuilder.type.shortLabel'),
                                dataIndex: 'editor_Type',
                                minWidth: TABLE_COL_MIN_WIDTH_ID
                            }
                            ]}
                            actions={[
                                (item) => ({
                                    ...BUTTON_DETAIL,
                                    title: item.name.startsWith(prefix) ? t('commons.actions.detailReadOnly.title') : t('commons.actions.detail.title'),
                                    onClick: (item) => {
                                        if (item.name.startsWith(prefix)) {
                                            setDisableModify(true);
                                            setIsDataflowBuilder(true)
                                        } else {
                                            setDisableModify(false);
                                            setIsDataflowBuilder(false)
                                        }
                                        onModalShow(MODIFY_MODE, item.id)
                                        getDataset(item)
                                    }
                                }),
                                (item) => (!item.name.startsWith(prefix)) ? (
                                    {
                                        ...BUTTON_DELETE,
                                        title: t('commons.actions.delete.title'),
                                        onClick: (item, deselect) => {
                                            if (item.hasMappingsets) {
                                                return Modal.warning({
                                                    title: t('data.datasetBuilder.datasetDeleteModal.errorTitle', {
                                                        datasetName: item.name
                                                    }),
                                                    content: t('data.datasetBuilder.datasetDeleteModal.hasMappingsetErrorMessage')
                                                })
                                            }
                                            if (item.name.startsWith(prefix)) {
                                                return Modal.warning({
                                                    title: t('data.datasetBuilder.datasetDeleteModal.errorTitle', {
                                                        datasetName: item.name
                                                    }),
                                                    content: t('data.datasetBuilder.datasetDeleteModal.fromDataflowBuilderErrorMessage')
                                                })
                                            }
                                            return Modal.confirm({
                                                title: t('data.datasetBuilder.datasetDeleteModal.title', {
                                                    datasetName: item.name
                                                }),
                                                onOk() {
                                                    deselect();
                                                    deleteDataset(item.id);
                                                },
                                                cancelText: t('commons.buttons.cancel.title')
                                            })
                                        }
                                    }) : null
                            ]}
                            rightActions={<Row type="flex" align="middle" justify="end" gutter={GUTTER_SM}>
                                <Col>
                                    <ConnectionSelector style={{minWidth: "40vw"}}/>
                                </Col>
                                <Col>
                                    <Tooltip
                                        title={t("scenes.mappingHandler.DatasetBuilder.tooltip.createNewDataset")}>
                                        <Button type="primary" icon="plus" onClick={() => onModalShow(CREATE_MODE)}>
                                            {t('commons.buttons.create.title')}
                                        </Button>
                                    </Tooltip>
                                </Col>
                            </Row>}
                        />
                    </Call>
                </Call>
            </Call>
        </Fragment>
    )
}

export default compose(
    translate(),
    connect(mapStateToProps, mapDispatchToProps)
)(DatasetBuilder);